import {query, transition, trigger, useAnimation} from '@angular/animations';
import {Component, OnInit} from '@angular/core';
import {FormBuilder} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {
  faBan,
  faBell,
  faBuildings,
  faCalendarCheck,
  faCheck, faComment,
  faEye,
  faFileContract,
  faFolderOpen,
} from '@fortawesome/pro-duotone-svg-icons';
import {
  ActionsEnum,
  AdvancedFilterField,
  ButtonTypeEnum,
  DeviceType,
  IspColoursEnum,
  RoutingData,
  ScreenModel,
  ScreenSizeService,
  SizeEnum,
  slideIn50StaggerAnimation,
} from '@i-supplier/angular-shared-module';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import * as moment from 'moment/moment';
import {BsModalService} from 'ngx-bootstrap/modal';
import {ToastrService} from 'ngx-toastr';
import {fadeOutHeightAnimation} from '../../shared/animation/common.animation';
import {ListContentComponent} from '../../shared/components/list-content/list-content.component';
import {ModalConfirmComponent} from '../../shared/components/modal-confirm/modal-confirm.component';
import {ListContentModel} from '../../shared/models/list-content.model';
import {AdvancedFilterData, ColumnsEnum} from '../activity-report.config';
import {ActivitySheetModel, ActivitySheetStatusEnum} from '../models/activity-sheet.model';
import {ActivityReportService} from '../services/activity-report.service';

@Component({
  selector: 'app-activity-sheet-list',
  templateUrl: './activity-sheet-list.component.html',
  styleUrls: ['./activity-sheet-list.component.scss'],
  animations: [
    trigger('fadeOut', [
      transition(':leave', [
        useAnimation(fadeOutHeightAnimation),
      ]),
    ]),
    trigger('slideInAnimation', [
      transition(':enter', [
        query('.slide-in-animation', [
          useAnimation(slideIn50StaggerAnimation),
        ], {optional: true}),
      ]),
    ]),
  ],
})
export class ActivitySheetListComponent extends ListContentComponent implements OnInit {

  advancedFilterData: AdvancedFilterField[] = AdvancedFilterData;
  listContent: ActivitySheetModel[] = [];
  routeData: RoutingData;
  currentScreenInfo: ScreenModel;
  loaders: { [key: string]: boolean } = {};
  allowedSizes = [8, 12, 20];
  listParamValidator = {
    page: RegExp('^[1-9][0-9]*$'),
    size: this.allowedSizes.map(size => size.toString()),
    sort: RegExp('^(projectName|purchaseOrderNumber|buyerCompanyName|supplierCompanyName|employeeName|startDate|period|submissionDate|validationDate|status)\,(asc|desc)$'),
    search: RegExp('.{3,}'),
    projectName: 'string',
    purchaseOrderNumber: 'string',
    buyerCompanyName: 'string',
    supplierCompanyName: 'string',
    employeeName: 'string',
    period: 'rangeDate',
    submissionDate: 'rangeDate',
    validationDate: 'rangeDate',
    quantity: 'number',
    status: RegExp(`^(${Object.values(ActivitySheetStatusEnum).join('|')})(\,(${Object.values(ActivitySheetStatusEnum).join('|')}))*$`),
  };

  size = this.allowedSizes[0];
  sort = ColumnsEnum.submissionDate;
  protected readonly ActivitySheetStatusEnum = ActivitySheetStatusEnum;
  protected readonly ButtonTypeEnum = ButtonTypeEnum;
  protected readonly IspColoursEnum = IspColoursEnum;
  protected readonly ColumnsEnum = ColumnsEnum;
  protected readonly ActionsEnum = ActionsEnum;
  protected readonly DeviceType = DeviceType;
  protected readonly SizeEnum = SizeEnum;
  protected readonly faEye = faEye;
  protected readonly faBan = faBan;
  protected readonly faBell = faBell;
  protected readonly faCheck = faCheck;
  protected readonly faComment = faComment;
  protected readonly faBuildings = faBuildings;
  protected readonly faFolderOpen = faFolderOpen;
  protected readonly faFileContract = faFileContract;
  protected readonly faCalendarCheck = faCalendarCheck;

  // Override method
  mapAdvancedSearchFormToQueryParam(advancedSearchForm: any): any {
    if (advancedSearchForm.hasOwnProperty(ColumnsEnum.status) && advancedSearchForm.status) {
      advancedSearchForm.status = advancedSearchForm.status.join();
    }
    if (advancedSearchForm.hasOwnProperty(ColumnsEnum.period) && advancedSearchForm.period) {
      advancedSearchForm.period = advancedSearchForm.period.map(date => moment(date).format('YYYY-MM-DD')).join();
    }
    if (advancedSearchForm.hasOwnProperty(ColumnsEnum.submissionDate) && advancedSearchForm.submissionDate) {
      advancedSearchForm.submissionDate = advancedSearchForm.submissionDate.map(date => moment(date).format('YYYY-MM-DD')).join();
    }
    if (advancedSearchForm.hasOwnProperty(ColumnsEnum.validationDate) && advancedSearchForm.validationDate) {
      advancedSearchForm.validationDate = advancedSearchForm.validationDate.map(date => moment(date).format('YYYY-MM-DD')).join();
    }

    return advancedSearchForm;
  }

  // Override method
  mapQueryParamToAdvancedSearchForm(params: any): void {
    Object.keys(params).forEach(paramKey => {
      if (this.advancedSearchForm.contains(paramKey)) {
        if (paramKey === ColumnsEnum.status) {
          const status = params[paramKey].split(',');
          this.advancedSearchForm.get(ColumnsEnum.status).setValue(_.uniq(status));
        } else if (paramKey === ColumnsEnum.period) {
          this.advancedSearchForm.get(ColumnsEnum.period).setValue(params[paramKey].split(',').map(date => new Date(date)));
        } else if (paramKey === ColumnsEnum.submissionDate) {
          this.advancedSearchForm.get(ColumnsEnum.submissionDate).setValue(params[paramKey].split(',').map(date => new Date(date)));
        } else if (paramKey === ColumnsEnum.validationDate) {
          this.advancedSearchForm.get(ColumnsEnum.validationDate).setValue(params[paramKey].split(',').map(date => new Date(date)));
        } else {
          this.advancedSearchForm.get(paramKey).setValue(params[paramKey]);
        }
        this.isAdvancedSearchDisplayed = true;
        this.isSearchActive = true;
      }
    });
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.routeData = this.route.snapshot.data;
    this.changeDefaultSort();

    this.route.data.subscribe((routeData: RoutingData) => {
      this.routeData = routeData;
      this.changeDefaultSort();
      super.subscribeToQueryParam();
    });

    this.screenSizeService.currentScreenInfo$.subscribe((data: ScreenModel) => this.currentScreenInfo = data);
  }

  retrieveListContent(params: any): void {
    this.loaders['retrieveListContent'] = true;
    const page = params.page ? params.page - 1 : 0;
    const size = params.size ? params.size : 8;
    const search = params.search ? params.search : null;
    const advancedSearch = this.activityReportService.mapAdvancedSearchForm(this.advancedSearchForm.value);
    let tmpSort = null;
    this.sortDirection = this.sortDirection ?? 'desc';
    switch (this.sort) {
      case ColumnsEnum.projectName:
        tmpSort = 'buyer.projectName';
        break;
      case ColumnsEnum.employeeName:
        tmpSort = 'supplier.employeeName';
        break;
      case null:
        tmpSort = 'submissionDate';
        break;
      default:
        tmpSort = this.sort;
        break;
    }
    tmpSort += ',createdDate,' + this.sortDirection;
    if (this.routeData?.status) {
      advancedSearch.status = this.routeData.status;
    }
    this.activityReportService.getActivitySheetsByPage(
      page,
      size,
      tmpSort,
      search,
      advancedSearch,
    ).subscribe({
      next: (res: ListContentModel) => {
        this.listContent = res.content;
        this.totalElements = res.totalElements;
        this.numberOfElements = res.numberOfElements;
        this.isListEmpty = !this.isSearchActive && res.empty;
        if (res.totalPages !== 0 && params.page > res.totalPages) {
          this.updateQueryParam({page: res.totalPages});
        }
        this.firstCallDone = true;
        this.loaders['retrieveListContent'] = false;
      },
      error: () => {
        this.loaders['retrieveListContent'] = false;
        this.toastrService.error(this.translateService.instant('global.ts.error'));
      },
    });
  }

  constructor(
    public router: Router,
    public route: ActivatedRoute,
    private activityReportService: ActivityReportService,
    private _formBuilder: FormBuilder,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private modalService: BsModalService,
    private screenSizeService: ScreenSizeService,
  ) {
    super(router, route);
    this.advancedSearchForm = this._formBuilder.group(this.advancedFilterData.reduce((controls, filter) => {
      controls[filter.control] = [null];
      return controls;
    }, {}));
  }

  changeDefaultSort() {
    this.sort = this.routeData.status === ActivitySheetStatusEnum.DRAFT ? ColumnsEnum.startDate : ColumnsEnum.submissionDate;
    this.sortDirection = 'desc';
  }

  resetSearch() {
    const params = {};
    for (const key of Object.keys(this.listParamValidator)) {
      params[key] = key === 'page' ? 1 : null;
    }
    params['size'] = this.size;
    params['sort'] = this.sort;
    this.updateQueryParam(params);
    this.advancedSearchForm.reset();
    this.basicSearch = null;
    this.retrieveListContent({});
  }

  checkColumn(name: ColumnsEnum): boolean {
    return this.routeData.columns.some(col => col.name.includes(name));
  }

  getColumnKey(name: ColumnsEnum): string {
    return this.routeData.columns.find(col => col.name === name).key;
  }

  sendReminder(activitySheet: ActivitySheetModel) {
    const initialState = {
      message: this.translateService.instant('activity-report.activity-sheet.modal.reminder.prompt'),
    };
    const modalRef = this.modalService.show(ModalConfirmComponent, {
      class: 'modal-lg',
      initialState,
    });
    this.subscriptions.push(modalRef.content.actionConfirmed.subscribe(() => {
      this.activityReportService.sendReminder(activitySheet.id).subscribe({
        next: () => {
          this.toastrService.info(this.translateService.instant('activity-report.activity-sheet.modal.reminder.success'));
          activitySheet.reminderAvailable = false;
        },
        error: () => this.toastrService.error(this.translateService.instant('global.ts.error')),
      });
    }));
  }

  confirmTimesheetModeration(status: ActivitySheetStatusEnum, activitySheet: ActivitySheetModel) {
    const textData: { prompt: string, placeholder: string } = {prompt: '', placeholder: ''};
    const params: {
      status: ActivitySheetStatusEnum,
      refusalComment: string,
      approvalComment: string
    } = {status: null as unknown as ActivitySheetStatusEnum, refusalComment: null, approvalComment: null};

    if (status === ActivitySheetStatusEnum.CANCELLED) {
      params.status = activitySheet.status === ActivitySheetStatusEnum.SUBMITTED ? ActivitySheetStatusEnum.DRAFT : ActivitySheetStatusEnum.CANCELLED;
      textData.prompt = this.translateService.instant('activity-report.activity-sheet.modal.cancel.prompt');
      textData.placeholder = this.translateService.instant('shared.components.modal-confirm.form.comment-placeholder');
    } else if (status === ActivitySheetStatusEnum.VALIDATED) {
      params.status = status;
      textData.prompt = this.translateService.instant('activity-report.activity-sheet.modal.validate.prompt');
      textData.placeholder = this.translateService.instant('activity-report.activity-sheet.modal.validate.placeholder');
    } else if (status === ActivitySheetStatusEnum.SUBMITTED) {
      params.status = status;
      textData.prompt = this.translateService.instant('activity-report.activity-sheet.modal.submit-zero.prompt');
    }

    const initialState = {
      message: textData.prompt,
      placeholder: textData.placeholder,
      commentRequired: status !== ActivitySheetStatusEnum.SUBMITTED,
      commentMinLength: 10,
      commentMaxLength: 255,
    };
    const modalRef = this.modalService.show(ModalConfirmComponent, {
      class: 'modal-lg',
      initialState,
    });
    this.subscriptions.push(modalRef.content.actionConfirmed.subscribe(
      comment => {
        params[status === ActivitySheetStatusEnum.CANCELLED ? 'refusalComment' : 'approvalComment'] = comment;
        this.moderateActivitySheet(status, params, activitySheet);
      },
    ));
  }

  moderateActivitySheet(status: ActivitySheetStatusEnum, params, activitySheet: ActivitySheetModel) {
    this.activityReportService.moderateActivitySheet(activitySheet, params).subscribe({
      next: (data: any) => {
        activitySheet.status = data.status;
        const statusToToastrCodeMap = {
          [ActivitySheetStatusEnum.VALIDATED]: 'validate',
          [ActivitySheetStatusEnum.CANCELLED]: 'cancel',
          [ActivitySheetStatusEnum.SUBMITTED]: 'submit-zero',
        };
        const toastrCode = statusToToastrCodeMap[status];
        this.toastrService.success(this.translateService.instant(`activity-report.activity-sheet.modal.${toastrCode}.success`));
        this.refreshListContent();
      },
      error: () => this.toastrService.error(this.translateService.instant('global.ts.error')),
    });
  }


  displayCancel(activitySheet: ActivitySheetModel) {
    const {status, invoiceGenerated} = activitySheet;
    return !invoiceGenerated
      && ![ActivitySheetStatusEnum.DRAFT, ActivitySheetStatusEnum.REFUSED, ActivitySheetStatusEnum.CANCELLED].includes(status);
  }
}
